// frontend/src/components/LiveModePanel.js
// ----------------------------------------------------
// ✅ Redesigned Live Mode Dashboard with Modern Card UI
// - Market & Strategy + Active Trades cards side by side
// - Professional SL History table with previous days
// - All existing functionality preserved
// ----------------------------------------------------

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import { socket } from "../App";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import debounce from "lodash.debounce";
import TradeLogs from "./TradeLogs";

export default function LiveModePanel({ kiteConnected }) {
  const [status, setStatus] = useState("Disconnected");
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [positions, setPositions] = useState([]);
  const [monthPnL, setMonthPnL] = useState(0);
  const [weekPnL, setWeekPnL] = useState(0);
  const [yearPnL, setYearPnL] = useState(0);
  const [lastTickTime, setLastTickTime] = useState(null);
  const [realizedPnL, setRealizedPnL] = useState(0);
  const [unrealizedPnL, setUnrealizedPnL] = useState(0);
  const [slState, setSlState] = useState([]);
  const [slHistory, setSlHistory] = useState([]);
  const [slPositions, setSlPositions] = useState([]);
  const [availableCash, setAvailableCash] = useState(0);

  // Market data state for Market & Strategy card
  const [marketData, setMarketData] = useState({
    symbol: "NIFTY",
    spot: 0,
    bias: "NEUTRAL",  // CALL | PUT | NEUTRAL
    indexRsi: 0,      // 5-min NIFTY RSI
    itm: null,        // { type, strike, premium, rsi, tradingsymbol }
  });

  const tickBuffer = useRef([]);
  const animationFrame = useRef(null);
  const ltpHistory = useRef({});
  const lastTickRef = useRef(Date.now());

  // ✅ Debounced + diff-safe SL update handler
  const handleSlUpdate = useMemo(
    () =>
      debounce((data) => {
        if (!data?.positions) return;

        // ---------- PATCH 2: SL HISTORY DIFF CHECK ----------
        if (data.history) {
          const histStr = JSON.stringify(data.history);
          if (histStr !== window.__LAST_SL_HIST_SNAPSHOT__) {
            window.__LAST_SL_HIST_SNAPSHOT__ = histStr;
            setSlHistory(data.history);
          }
        }
        // -----------------------------------------------------

        // Compare JSON snapshots to ignore identical updates
        const stableIncoming = data.positions.map(p => ({
          token: p.instrument_token,
          ltp: Number(p.ltp || 0),
          sl: Number(p.sl || 0),
          pnl: Number(p.pnl || 0),
          side: p.side,
          qty: p.qty,
          trail: p.trailActive || p.trail,
          symbol: p.symbol || p.tradingsymbol || "-",
          exited: p.exited
        }));

        const incomingStr = JSON.stringify(stableIncoming);
        if (incomingStr === window.__LAST_SL_POS_SNAPSHOT__) return;
        window.__LAST_SL_POS_SNAPSHOT__ = incomingStr;


        setSlPositions((prev) => {
          const incoming = data.positions;
          const updated = [...prev];
          let changed = false;


          for (const pos of incoming) {
            const i = updated.findIndex(
              (p) => p.instrument_token === pos.instrument_token
            );
            if (i >= 0) {
              const prevItem = updated[i];
              // ✅ safer numeric diff check (avoids rounding & key mismatch)
              const numDiff = (a, b) => Number(a || 0) !== Number(b || 0);

              const same =
                !numDiff(prevItem.ltp, pos.ltp) &&
                !numDiff(prevItem.sl, pos.sl) &&
                !numDiff(prevItem.pnl, pos.pnl) &&
                prevItem.exited === pos.exited &&
                Boolean(prevItem.trailActive || prevItem.trail) === Boolean(pos.trailActive || pos.trail);

              if (!same) {
                updated[i] = { ...prevItem, ...pos };
                changed = true;
              }
            } else {
              updated.push(pos);
              changed = true;
            }
          }


          return changed ? updated : prev;
        });
      }, 250),
    []
  );


  // ✅ Stable LTP Buffer Flush (batched visual updates)
  const flushBuffer = useCallback(() => {
    if (tickBuffer.current.length === 0) {
      animationFrame.current = requestAnimationFrame(flushBuffer);
      return;
    }

    const batch = [...tickBuffer.current];
    tickBuffer.current = [];

    setPositions((prev) => {
      let updated = false;
      const copy = [...prev];

      for (const tick of batch) {
        const idx = copy.findIndex((p) => p.instrument_token === tick.instrument_token);
        if (idx >= 0) {
          const pos = copy[idx];
          const newLtp = tick.last_price || tick.ltp;
          const entry = pos.average_price || 0;
          const qty = pos.quantity || 0;

          // Keep LTP history for sparkline
          const token = pos.instrument_token;
          if (!ltpHistory.current[token]) ltpHistory.current[token] = [];
          ltpHistory.current[token].push(newLtp);
          if (ltpHistory.current[token].length > 30) ltpHistory.current[token].shift();

          const newPnL = (newLtp - entry) * qty;
          if (pos.ltp !== newLtp || pos.pnl !== newPnL) {
            copy[idx] = { ...pos, ltp: newLtp, pnl: newPnL };
            updated = true;
          }
        }
      }
      return updated ? copy : prev;
    });

    animationFrame.current = requestAnimationFrame(flushBuffer);
  }, []);

  useEffect(() => {
    animationFrame.current = requestAnimationFrame(flushBuffer);
    return () => cancelAnimationFrame(animationFrame.current);
  }, [flushBuffer]);


  // ✅ Combined Socket + Fetch Setup
  useEffect(() => {
    const handleTick = (tick) => {
      if (!tick || !tick.instrument_token) return;
      lastTickRef.current = Date.now();
      setLastTickTime(Date.now());
      tickBuffer.current.push(tick);
    };

    socket.on("tick", handleTick);
    socket.on("sl_state_update", handleSlUpdate);

    return () => {
      socket.off("tick", handleTick);
      socket.off("sl_state_update", handleSlUpdate);
    };
  }, [handleSlUpdate]);

  // ✅ Initial Data Fetch + Polling
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [{ data: stratRes }, { data: posRes }, { data: ordRes }, { data: pnlRes }, { data: slRes }, { data: portRes }] = await Promise.all([
          axios.get(`${API_BASE}/strategy/status`),
          axios.get(`${API_BASE}/kite/positions`),
          axios.get(`${API_BASE}/kite/orders`),
          axios.get(`${API_BASE}/kite/pnl-summary`),
          axios.get(`${API_BASE}/kite/sl-state`),
          axios.get(`${API_BASE}/kite/portfolio`),
        ]);

        if (stratRes?.ok) {
          // Only consider Live running if mode is 'live' or 'both'
          const isLiveActive = stratRes.schedulerRunning &&
            (stratRes.schedulerMode === "live" || stratRes.schedulerMode === "both");

          setSchedulerRunning(isLiveActive);
          setStatus(isLiveActive ? `Running (${stratRes.schedulerMode})` : "Stopped");
        }

        if (posRes?.ok) {
          const filled = posRes.positions.filter((p) => p.quantity !== 0);
          setPositions((prev) => {
            const updated = filled.map((p) => {
              const old = prev.find((o) => o.instrument_token === p.instrument_token);
              return { ...p, ltp: old?.ltp || p.last_price || 0 };
            });
            // Only update if data actually changed
            const prevStr = JSON.stringify(prev.map(p => ({ t: p.instrument_token, q: p.quantity })));
            const newStr = JSON.stringify(updated.map(p => ({ t: p.instrument_token, q: p.quantity })));
            return prevStr !== newStr ? updated : prev;
          });
        }

        if (ordRes?.ok) setOrders(ordRes.orders.slice(0, 20));
        if (pnlRes?.ok) {
          setMonthPnL(pnlRes.monthPnL || 0);
          setWeekPnL(pnlRes.weekPnL || 0);
          setYearPnL(pnlRes.yearPnL || 0);
        }

        if (slRes?.ok) {
          if (slRes.activeSL) setSlPositions(slRes.activeSL);
          if (slRes.history) setSlHistory(slRes.history);
        }

        if (portRes?.ok) {
          setAvailableCash(portRes.equity || 0);
          // Today's P&L from portfolio (realized + unrealized from positions)
          setRealizedPnL(portRes.pnl || portRes.todayPnL || 0);
        }
      } catch (err) {
        console.error("❌ Dashboard fetch error:", err.message);
      }
    };

    fetchAll();
    const interval = setInterval(fetchAll, 5000);
    return () => clearInterval(interval);
  }, []);

  // ✅ Fetch Market Data (Bias, RSI, ITM) for Market & Strategy Card
  useEffect(() => {
    let cancelled = false;
    const fetchMarketData = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/strategy/market-data`);

        if (!cancelled && data?.ok) {
          const incoming = {
            symbol: data.symbol || "NIFTY",
            spot: data.spot || 0,
            bias: data.bias || "NEUTRAL",
            indexRsi: data.indexRsi || 0,
            itm: data.itm || null,
          };
          setMarketData(prev => {
            // Only update if data actually changed
            if (prev.spot === incoming.spot &&
              prev.bias === incoming.bias &&
              prev.indexRsi === incoming.indexRsi &&
              prev.itm?.strike === incoming.itm?.strike &&
              prev.itm?.rsi === incoming.itm?.rsi) {
              return prev;
            }
            return incoming;
          });
        }
      } catch (err) {
        // silent - market data is optional
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);


  // ✅ Scheduler Controls
  const startLive = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/strategy/start`, { mode: "live" });
      setSchedulerRunning(true);
      setStatus("Running (Live)");
    } catch (err) {
      console.error("❌ Start live error:", err.message);
    }
    setLoading(false);
  };

  const stopLive = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/strategy/stop`, { mode: "live" });
      setSchedulerRunning(false);
      setStatus("Stopped");
    } catch (err) {
      console.error("❌ Stop live error:", err.message);
    }
    setLoading(false);
  };


  // ✅ Exit Position Handler
  const exitPosition = async (tradingsymbol) => {
    if (!window.confirm(`Exit ${tradingsymbol}?`)) return;
    try {
      const res = await axios.post(`${API_BASE}/kite/sl-state/exit`, { tradingsymbol });
      if (res.data?.ok) {
        setPositions((p) =>
          p.map((pos) =>
            pos.tradingsymbol === tradingsymbol ? { ...pos, status: "Exited" } : pos
          )
        );
      }
    } catch (err) {
      alert(`Exit failed: ${err.message}`);
    }
  };





  const totalPnL = realizedPnL + unrealizedPnL;

  // Determine bias from positions
  const activeTrades = slPositions.filter(p => !p.exited && p.side);
  const hasCE = activeTrades.some(p => p.symbol?.includes('CE'));
  const hasPE = activeTrades.some(p => p.symbol?.includes('PE'));

  // ----------------------------------------------------
  // Render
  // ----------------------------------------------------
  return (
    <div className="live-dashboard">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total P&L</div>
          <div className={`stat-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {totalPnL >= 0 ? '+' : ''}₹{totalPnL.toFixed(2)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Available Cash</div>
          <div className="stat-value" style={{ color: 'var(--color-accent)' }}>
            ₹{availableCash.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Not connected warning */}
      {kiteConnected === false && (
        <div className="warning-banner" style={{ marginBottom: '20px' }}>
          <span>⚠️</span>
          <span>Please login to Zerodha to start live trading. Click "Login to Zerodha" button above.</span>
        </div>
      )}

      {/* Status & Controls */}
      <div className="dashboard-controls">
        <div className="status-display">
          <span className={`status-dot ${schedulerRunning ? 'online' : 'offline'}`}></span>
          <span className={schedulerRunning ? 'text-success' : 'text-danger'}>
            {status}
          </span>
          <span className="last-tick-info font-mono" style={{ marginLeft: '16px', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
            Last Tick: {lastTickTime
              ? new Date(lastTickTime).toLocaleTimeString("en-IN", { hour12: false })
              : "Waiting..."}
          </span>
        </div>
        <div className="control-buttons">
          <button
            onClick={startLive}
            disabled={schedulerRunning || loading || kiteConnected === false}
            className={`btn ${schedulerRunning ? 'btn-outline' : 'btn-success'}`}
            title={kiteConnected === false ? "Login to Zerodha first" : ""}
          >
            <span>▶️</span> Start Scheduler
          </button>
          <button
            onClick={stopLive}
            disabled={!schedulerRunning || loading}
            className={`btn ${schedulerRunning ? 'btn-danger' : 'btn-outline'}`}
          >
            <span>⏹</span> Stop Scheduler
          </button>
        </div>
      </div>

      {/* Market & Strategy + Active Trades Cards */}
      <div className="cards-row">
        <MarketStrategyCard marketData={marketData} />
        <ActiveTradesCard trades={activeTrades} ltpHistoryRef={ltpHistory} exitPosition={exitPosition} />
      </div>

      {/* Trade Logs */}
      <TradeLogs />

      {/* Live SL Monitor */}
      <TableSL slState={slPositions} />

      {/* Trade History - Shows previous days */}
      <TradeHistoryTable slHistory={slHistory} />

      {/* Recent Orders */}
      <TableOrders orders={orders} />

      <style>{`
        .live-dashboard {
          color: var(--color-text-primary);
        }

        .dashboard-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          margin-bottom: 24px;
        }

        .status-display {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 500;
        }

        .control-buttons {
          display: flex;
          gap: 10px;
        }

        .warning-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(250, 204, 21, 0.15);
          border: 1px solid rgba(250, 204, 21, 0.3);
          border-radius: var(--radius-md);
          color: #facc15;
          font-weight: 500;
        }

        .cards-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 32px;
        }

        @media (max-width: 900px) {
          .cards-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}


// ----------------------------------------------------
// 📊 Market & Strategy Card
// ----------------------------------------------------
function MarketStrategyCard({ marketData }) {
  const isCall = marketData.bias === "CALL";
  const isPut = marketData.bias === "PUT";
  const isNeutral = !isCall && !isPut;

  // Determine what to show for ITM based on bias
  const activeItm = marketData.itm;

  return (
    <div className="market-card">
      <div className="card-header">
        <span className="card-icon">📊</span>
        <h3>Current Market</h3>
      </div>

      <div className="market-inner-card">
        <div className="market-top-row">
          <div className="market-symbol">{marketData.symbol || "NIFTY"}</div>
          <span className={`bias-badge ${isPut ? 'bearish' : isCall ? 'bullish' : 'neutral'}`}>
            {isCall ? "📈 CALL" : isPut ? "📉 PUT" : "⏸️ NEUTRAL"}
          </span>
        </div>

        <div className="spot-row">
          <span className="spot-label">Spot</span>
          <span className="spot-value">₹{(marketData.spot || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</span>
        </div>

        <div className="ema-row">
          {/* Show Index RSI (5-min) */}
          <div className="ema-item">
            <span className="ema-label">Index RSI</span>
            <span className={`ema-value ${(marketData.indexRsi || 0) > 60 ? 'rsi-high' : (marketData.indexRsi || 0) < 40 ? 'rsi-low' : ''}`}>
              {(marketData.indexRsi || 0).toFixed(1)}
            </span>
          </div>

          {/* Show ITM based on bias - hide if neutral */}
          {activeItm ? (
            <>
              <div className="ema-item itm-item">
                <span className="ema-label">ITM {activeItm.type}</span>
                <span className={`ema-value ${activeItm.type === 'CE' ? 'itm-ce' : 'itm-pe'}`}>
                  {activeItm.strike}
                  {activeItm.premium && (
                    <span className="itm-premium">₹{activeItm.premium.toFixed(2)}</span>
                  )}
                </span>
              </div>
              <div className="ema-item">
                <span className="ema-label">ITM RSI</span>
                <span className={`ema-value ${(activeItm.rsi || 0) > 60 ? 'rsi-high' : (activeItm.rsi || 0) < 40 ? 'rsi-low' : ''}`}>
                  {(activeItm.rsi || 0).toFixed(1)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="ema-item">
                <span className="ema-label">ITM</span>
                <span className="ema-value" style={{ color: 'var(--color-text-muted)' }}>
                  Waiting for trade...
                </span>
              </div>
              <div className="ema-item">
                <span className="ema-label">ITM RSI</span>
                <span className="ema-value" style={{ color: 'var(--color-text-muted)' }}>
                  -
                </span>
              </div>
            </>
          )}
        </div>

        {marketData.lastSignal && (
          <div className="signal-box">
            <span className="signal-label">Last Signal</span>
            <div className="signal-value">{marketData.lastSignal}</div>
            {marketData.lastSignalTime && (
              <span className="signal-time">{marketData.lastSignalTime}</span>
            )}
          </div>
        )}
      </div>

      <style>{`
        .market-card {
          background: var(--color-bg-card);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-lg);
          padding: 20px;
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }

        .card-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .card-icon {
          font-size: 1.2rem;
        }

        .market-inner-card {
          background: rgba(0,0,0,0.3);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: 16px;
        }

        .market-top-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .market-symbol {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .bias-badge {
          padding: 4px 12px;
          border-radius: var(--radius-full);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .bias-badge.bullish {
          background: var(--color-success-bg);
          color: var(--color-success-light);
        }

        .bias-badge.bearish {
          background: var(--color-danger-bg);
          color: var(--color-danger-light);
        }

        .bias-badge.neutral {
          background: rgba(255,255,255,0.1);
          color: var(--color-text-secondary);
        }

        .spot-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 16px;
        }

        .spot-label {
          color: var(--color-text-muted);
          font-size: 0.85rem;
        }

        .spot-value {
          font-size: 1.5rem;
          font-weight: 700;
          font-family: var(--font-family-mono);
          color: var(--color-text-primary);
        }

        .ema-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          padding: 12px 0;
          border-top: 1px solid var(--color-border);
          border-bottom: 1px solid var(--color-border);
        }

        .ema-item {
          text-align: center;
        }

        .ema-label {
          display: block;
          font-size: 0.75rem;
          color: var(--color-text-muted);
          margin-bottom: 4px;
        }

        .ema-value {
          font-family: var(--font-family-mono);
          font-weight: 600;
          color: var(--color-text-secondary);
        }

        .ema-value.rsi-high {
          color: var(--color-danger-light);
        }

        .ema-value.rsi-low {
          color: var(--color-success-light);
        }

        .itm-item {
          flex: 1;
        }

        .itm-ce {
          color: var(--color-success-light);
        }

        .itm-pe {
          color: var(--color-danger-light);
        }

        .itm-premium {
          display: block;
          font-size: 0.8rem;
          color: var(--color-text-muted);
          margin-top: 2px;
        }

        .signal-box {
          margin-top: 16px;
          padding: 12px;
          background: rgba(255,255,255,0.03);
          border-radius: var(--radius-sm);
          text-align: center;
        }

        .signal-label {
          display: block;
          font-size: 0.75rem;
          color: var(--color-text-muted);
          margin-bottom: 6px;
        }

        .signal-value {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }

        .signal-time {
          display: block;
          font-size: 0.75rem;
          color: var(--color-text-muted);
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
}


// ----------------------------------------------------
// 📦 Active Trades Card
// ----------------------------------------------------
function ActiveTradesCard({ trades, ltpHistoryRef, exitPosition }) {
  const hasTrades = trades && trades.length > 0;

  return (
    <div className="trades-card">
      <div className="card-header">
        <span className="card-icon">📦</span>
        <h3>Open Position</h3>
        {hasTrades && <span className="position-count">{trades.length}</span>}
      </div>

      {!hasTrades ? (
        <div className="empty-trades">
          <div className="empty-icon-container">
            <div className="empty-icon-ring"></div>
            <span className="empty-icon">🔍</span>
          </div>
          <p>Waiting for trade...</p>
        </div>
      ) : (
        <div className="trades-list">
          {trades.map((trade, i) => {
            const pnl = Number(trade.pnl || 0);
            const isProfit = pnl >= 0;
            return (
              <div key={trade.instrument_token || i} className={`trade-item ${isProfit ? 'profit' : 'loss'}`}>
                <div className="trade-header">
                  <div className="trade-symbol">{trade.symbol || trade.tradingsymbol}</div>
                  <span className={`trade-side ${trade.side === 'BUY' ? 'buy' : 'sell'}`}>
                    {trade.side}
                  </span>
                  <div style={{ marginLeft: 'auto', marginRight: '10px' }}>
                    <Sparkline token={trade.instrument_token} ltpHistoryRef={ltpHistoryRef} />
                  </div>
                </div>

                <div className="trade-stats">
                  <div className="stat-item">
                    <span className="stat-label">Qty</span>
                    <span className="stat-value">{trade.qty}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">LTP</span>
                    <span className="stat-value">₹{Number(trade.ltp || 0).toFixed(2)}</span>
                  </div>
                </div>

                <div className="trade-pnl-container">
                  <span className={`trade-pnl ${isProfit ? 'positive' : 'negative'}`}>
                    {isProfit ? '+' : ''}₹{pnl.toFixed(2)}
                  </span>

                </div>

                <button
                  onClick={() => exitPosition(trade.symbol || trade.tradingsymbol)}
                  className="exit-btn"
                >
                  Exit
                </button>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .trades-card {
          background: linear-gradient(145deg, rgba(30, 32, 45, 0.95) 0%, rgba(20, 22, 35, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 24px;
          min-height: 280px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .card-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #fff;
          flex: 1;
        }

        .card-icon {
          font-size: 1.3rem;
        }

        .position-count {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 0.75rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 12px;
        }

        .empty-trades {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 50px 20px;
        }

        .empty-icon-container {
          position: relative;
          margin-bottom: 20px;
        }

        .empty-icon-ring {
          position: absolute;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          border: 2px solid rgba(102, 126, 234, 0.3);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: pulse-ring 2s ease-out infinite;
        }

        @keyframes pulse-ring {
          0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1.3); opacity: 0; }
        }

        .empty-icon {
          font-size: 2.5rem;
          display: block;
          position: relative;
          z-index: 1;
        }

        .empty-trades p {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.5);
          margin: 0;
        }

        .trades-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .trade-item {
          background: linear-gradient(135deg, rgba(40, 44, 60, 0.6) 0%, rgba(30, 34, 50, 0.8) 100%);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition: all 0.3s ease;
        }

        .trade-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }

        .trade-item.profit {
          border-left: 3px solid #10b981;
        }

        .trade-item.loss {
          border-left: 3px solid #ef4444;
        }

        .trade-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .trade-symbol {
          font-weight: 700;
          font-size: 1rem;
          color: #fff;
          font-family: 'SF Mono', 'Consolas', monospace;
          letter-spacing: 0.5px;
        }

        .trade-side {
          padding: 5px 12px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .trade-side.buy {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .trade-side.sell {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .trade-stats {
          display: flex;
          gap: 24px;
          margin-bottom: 16px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stat-label {
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.4);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .stat-value {
          font-size: 0.95rem;
          color: #fff;
          font-weight: 600;
          font-family: 'SF Mono', 'Consolas', monospace;
        }

        .trade-pnl-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          padding: 12px 16px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
        }

        .trade-pnl {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .trade-pnl.positive {
          color: #10b981;
          text-shadow: 0 0 20px rgba(16, 185, 129, 0.4);
        }

        .trade-pnl.negative {
          color: #ef4444;
          text-shadow: 0 0 20px rgba(239, 68, 68, 0.4);
        }

        .exit-btn {
          width: 100%;
          padding: 12px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .exit-btn:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
        }

        .exit-btn:active {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}


// ----------------------------------------------------
// 🧠 Live SL Monitor Table
// ----------------------------------------------------
function TableSL({ slState }) {
  if (!slState || slState.length === 0) {
    return null; // Don't show if empty
  }

  return (
    <div className="table-section">
      <h4 className="section-title">
        <span>🧠</span> Live SL Monitor
      </h4>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Side</th>
              <th>Qty</th>
              <th>LTP</th>
              <th>SL</th>
              <th>Trail</th>
              <th>P&L</th>
            </tr>
          </thead>
          <tbody>
            {slState.map((s) => (
              <SLRow key={s.instrument_token} data={s} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 🧠 Memoized SL Row Component
const SLRow = React.memo(function SLRow({ data }) {
  const ltp = Number(data.ltp || 0);
  const sl = Number(data.sl || 0);
  const qty = Number(data.qty || 0);
  const entry = Number(data.entryPrice || data.entry || data.average_price || 0);
  const pnl =
    data.side === "BUY"
      ? (ltp - entry) * qty
      : (entry - ltp) * qty;

  const trail = data.trailActive || data.trail || false;

  return (
    <tr className={trail ? 'row-trail' : ''}>
      <td className="font-mono">{data.symbol || "-"}</td>
      <td className={data.side === "BUY" ? "text-success" : "text-danger"} style={{ fontWeight: 600 }}>
        {data.side}
      </td>
      <td>{data.qty ?? "-"}</td>
      <td className="font-mono">₹{ltp > 0 ? ltp.toFixed(2) : "-"}</td>
      <td className="font-mono">₹{sl > 0 ? sl.toFixed(2) : "-"}</td>
      <td>{trail ? <span className="trail-badge">Yes</span> : "No"}</td>
      <td className={`font-mono ${pnl >= 0 ? 'text-success' : 'text-danger'}`}>
        {pnl >= 0 ? '+' : ''}₹{pnl.toFixed(2)}
      </td>
    </tr>
  );
});


// ----------------------------------------------------
// 📜 Trade History Table (Shows Previous Days)
// ----------------------------------------------------
function TradeHistoryTable({ slHistory }) {
  // Group history by date and sort
  const groupedHistory = useMemo(() => {
    if (!slHistory || slHistory.length === 0) return [];

    // Create entries with additional calculated fields
    const entries = slHistory.map(h => {
      const date = new Date(h.ts);
      const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const entryTime = h.entryTime ? new Date(h.entryTime).toLocaleTimeString('en-IN', { hour12: false }) : '-';
      const exitTime = new Date(h.ts).toLocaleTimeString('en-IN', { hour12: false });

      // Calculate P&L
      const entryPrice = Number(h.entryPrice || h.entry || 0);
      const exitPrice = Number(h.ltp || 0);
      const qty = Number(h.qty || 1);
      const pnl = h.side === 'BUY'
        ? (exitPrice - entryPrice) * qty
        : (entryPrice - exitPrice) * qty;

      // Determine type from symbol
      const type = h.symbol?.includes('CE') ? 'CE' : h.symbol?.includes('PE') ? 'PE' : '-';

      return {
        ...h,
        dateStr,
        entryTime,
        exitTime,
        entryPrice,
        exitPrice,
        pnl,
        type,
        sortKey: date.getTime()
      };
    });

    // Sort by date descending
    entries.sort((a, b) => b.sortKey - a.sortKey);

    // Deduplicate
    const unique = [];
    const seen = new Set();
    for (const entry of entries) {
      const key = `${entry.symbol}-${entry.ts}-${entry.reason}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(entry);
      }
    }

    return unique;
  }, [slHistory]);

  if (groupedHistory.length === 0) {
    return (
      <div className="history-card">
        <div className="card-header">
          <span className="card-icon">📜</span>
          <h3>Trade History</h3>
        </div>
        <div className="table-wrapper history-table-wrapper">
          <table className="data-table history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entry Time</th>
                <th>Exit Time</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Entry Price</th>
                <th>Exit Price</th>
                <th>Max Profit (Pts)</th>
                <th>P&L (Amount)</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-muted)' }}>
                  <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px', opacity: 0.6 }}>📭</span>
                  No trade history yet.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="history-card">
      <div className="card-header">
        <span className="card-icon">📜</span>
        <h3>Trade History</h3>
      </div>
      <div className="table-wrapper history-table-wrapper">
        <table className="data-table history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Entry Time</th>
              <th>Exit Time</th>
              <th>Symbol</th>
              <th>Type</th>
              <th>Entry Price</th>
              <th>Exit Price</th>
              <th>Max Profit (Pts)</th>
              <th>P&L (Amount)</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {groupedHistory.map((h, i) => (
              <tr key={i}>
                <td>{h.dateStr}</td>
                <td className="font-mono">{h.entryTime}</td>
                <td className="font-mono">{h.exitTime}</td>
                <td className="font-mono symbol-cell">{h.symbol}</td>
                <td className={h.type === 'CE' ? 'text-success' : 'text-danger'}>{h.type}</td>
                <td className="font-mono">₹{h.entryPrice.toFixed(2)}</td>
                <td className="font-mono">₹{h.exitPrice.toFixed(2)}</td>
                <td className={`font-mono ${(h.maxProfit || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                  {h.maxProfit ? `+${h.maxProfit.toFixed(2)}` : '-'}
                </td>
                <td className={`font-mono pnl-cell ${h.pnl >= 0 ? 'positive' : 'negative'}`}>
                  {h.pnl >= 0 ? '+' : ''}₹{h.pnl.toFixed(2)}
                </td>
                <td>
                  <span className={`reason-badge ${h.reason?.toLowerCase().replace(/[^a-z]/g, '_')}`}>
                    {h.reason}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .history-card {
          background: rgba(20, 20, 30, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 32px;
        }

        .history-card .card-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }

        .history-card .card-header h3 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0;
        }

        .history-card .card-icon {
          font-size: 1.2rem;
        }

        .history-table-wrapper {
          overflow-x: auto;
          border-radius: var(--radius-md);
          border: 1px solid var(--color-border);
          background: rgba(0, 0, 0, 0.2);
        }

        .history-table {
          width: 100%;
          min-width: 1100px;
          border-collapse: collapse;
        }

        .history-table thead {
          background: rgba(255, 255, 255, 0.05);
        }

        .history-table th {
          padding: 14px 16px;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-secondary);
          border-bottom: 1px solid var(--color-border);
          white-space: nowrap;
        }

        .history-table td {
          padding: 12px 16px;
          white-space: nowrap;
          border-bottom: 1px solid rgba(255, 255, 255, 0.03);
        }

        .history-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.03);
        }


        .symbol-cell {
          color: var(--color-accent);
          font-weight: 500;
        }

        .pnl-cell.positive {
          color: var(--color-success-light);
          font-weight: 600;
        }

        .pnl-cell.negative {
          color: var(--color-danger-light);
          font-weight: 600;
        }

        .reason-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .reason-badge.trailing_sl_hit,
        .reason-badge.sl {
          background: var(--color-danger-bg);
          color: var(--color-danger-light);
        }

        .reason-badge.breakeven_exit {
          background: rgba(250, 204, 21, 0.15);
          color: #facc15;
        }

        .reason-badge.target_hit {
          background: var(--color-success-bg);
          color: var(--color-success-light);
        }

        .reason-badge.manual_exit {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }

        .trail-badge {
          background: var(--color-success-bg);
          color: var(--color-success-light);
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .row-trail {
          background: rgba(34, 197, 94, 0.05) !important;
        }

        .table-section {
          margin-bottom: 32px;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--color-border);
        }

        .table-wrapper {
          overflow-x: auto;
          border-radius: var(--radius-md);
          border: 1px solid var(--color-border);
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: var(--color-text-muted);
          text-align: center;
        }

        .empty-icon {
          font-size: 2.5rem;
          margin-bottom: 12px;
          opacity: 0.6;
        }
      `}</style>
    </div>
  );
}


// ----------------------------------------------------
// 🕒 Recent Orders Table
// ----------------------------------------------------
function TableOrders({ orders }) {
  if (!orders || orders.length === 0) {
    return null;
  }

  return (
    <div className="table-section">
      <h4 className="section-title">
        <span>🕒</span> Recent Orders
      </h4>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Instrument</th>
              <th>Product</th>
              <th>Qty</th>
              <th>Avg. Price</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o, i) => {
              const statusClass =
                o.status === "COMPLETE" ? "status-complete" :
                  o.status === "REJECTED" ? "status-rejected" :
                    "status-pending";
              const isExit = o.transaction_type === "SELL";
              return (
                <tr key={i} className={isExit ? 'row-exit' : ''}>
                  <td>{o.order_timestamp ? new Date(o.order_timestamp).toLocaleTimeString("en-IN", { hour12: false }) : "-"}</td>
                  <td className={o.transaction_type === "BUY" ? "text-success" : "text-danger"}>
                    {o.transaction_type}
                  </td>
                  <td className="font-mono">{o.tradingsymbol}</td>
                  <td>{o.product}</td>
                  <td>{o.quantity}</td>
                  <td className="font-mono">₹{o.average_price}</td>
                  <td className={statusClass}>{o.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        .row-exit {
          background: rgba(255, 255, 255, 0.02) !important;
        }

        .status-complete {
          color: var(--color-success-light);
          font-weight: 600;
        }

        .status-rejected {
          color: var(--color-danger-light);
          font-weight: 600;
        }

        .status-pending {
          color: #facc15;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

// ----------------------------------------------------
// 📈 Sparkline Component (Moved Outside)
// ----------------------------------------------------
const Sparkline = React.memo(({ token, ltpHistoryRef }) => {
  const data = (ltpHistoryRef.current[token] || []).map((v, i) => ({ x: i, y: v }));
  if (data.length < 2) return <span style={{ color: "#888" }}>—</span>;

  const direction = data[data.length - 1].y >= data[0].y;
  const color = direction ? "#22c55e" : "#ef4444";

  return (
    <ResponsiveContainer width={60} height={20}>
      <LineChart data={data}>
        <Line type="monotone" dataKey="y" stroke={color} strokeWidth={1.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
});
